import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import Toast from '../components/Toast';
import SearchableSelect from '../components/SearchableSelect';
import ConfirmModal from '../components/ConfirmModal';
import HandledOutsideModal, { handledReasonLabel } from '../components/HandledOutsideModal';
import PayrollNotes from '../components/PayrollNotes';
import StatusPill from '../components/StatusPill';
import ProgressBar from '../components/ProgressBar';
import { MONTH_NAMES, getUKBankHolidays } from '../utils/constants';

function isUKBankHoliday(date) {
  const holidays = getUKBankHolidays(date.getFullYear());
  return holidays.includes(new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime());
}

function rollBackToWorkingDay(date) {
  while (date.getDay() === 0 || date.getDay() === 6 || isUKBankHoliday(date)) {
    date.setDate(date.getDate() - 1);
  }
  return date;
}

// When `adjust` is true, a pay day landing on a weekend or bank holiday is
// brought forward to the previous working day. `last_working_day` always rolls
// back by definition regardless of the flag.
function getPayDayDate(payDayType, payDaySpecificDate, month, year, adjust = true) {
  if (!payDayType || !month || !year) return null;
  const m = parseInt(month) - 1;
  const y = parseInt(year);
  if (payDayType === 'specific_date' && payDaySpecificDate) {
    const d = new Date(y, m, payDaySpecificDate);
    return adjust ? rollBackToWorkingDay(d) : d;
  }
  if (payDayType === 'last_friday') {
    const lastDay = new Date(y, m + 1, 0);
    const dayOfWeek = lastDay.getDay();
    const diff = (dayOfWeek + 2) % 7;
    const friday = new Date(y, m, lastDay.getDate() - diff);
    if (adjust && isUKBankHoliday(friday)) {
      friday.setDate(friday.getDate() - 1);
      return rollBackToWorkingDay(friday);
    }
    return friday;
  }
  if (payDayType === 'last_day_of_month') {
    const d = new Date(y, m + 1, 0);
    return adjust ? rollBackToWorkingDay(d) : d;
  }
  const lastDay = new Date(y, m + 1, 0);
  return rollBackToWorkingDay(lastDay);
}

function formatPayDayDate(date) {
  if (!date) return '-';
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const d = date.getDate();
  const suffix = (d % 100 > 10 && d % 100 < 14) ? 'th'
    : d % 10 === 1 ? 'st' : d % 10 === 2 ? 'nd' : d % 10 === 3 ? 'rd' : 'th';
  return `${days[date.getDay()]} ${d}${suffix} ${months[date.getMonth()]}`;
}

function isWorkingDay(date) {
  return date.getDay() !== 0 && date.getDay() !== 6 && !isUKBankHoliday(date);
}

function getSubmissionDeadline(payDayType, payDaySpecificDate, month, year, deadlineDaysBefore, adjust = true) {
  const payDate = getPayDayDate(payDayType, payDaySpecificDate, month, year, adjust);
  if (!payDate) return null;
  const deadline = new Date(payDate);
  const daysToCount = deadlineDaysBefore || 5;
  let workingDays = 0;
  while (workingDays < daysToCount) {
    deadline.setDate(deadline.getDate() - 1);
    if (isWorkingDay(deadline)) workingDays++;
  }
  return deadline;
}

const TYPE_LABELS = {
  new_starter: 'New Starters', leaver: 'Leavers', salary: 'Salary Changes',
  pension: 'Pension Updates', parental_leave: 'Parental Leave', sick_leave: 'Sick Leave',
  unpaid_leave: 'Unpaid Leave',
  addition: 'Additions', deduction: 'Deductions', bank_details: 'Bank Details',
  address_change: 'Address Changes', contractual_change: 'Contractual Changes',
  personal_details: 'Personal Details',
};

function getChangeSummary(changes) {
  if (!changes || changes.length === 0) return 'No changes';
  const counts = {};
  for (const c of changes) {
    const label = TYPE_LABELS[c.type] || c.type;
    counts[label] = (counts[label] || 0) + 1;
  }
  return Object.entries(counts).map(([label, count]) => `${count} ${label}`).join(', ');
}

function isNewSubmission(submittedAt) {
  const submitted = new Date(submittedAt);
  const now = new Date();
  return (now - submitted) < 24 * 60 * 60 * 1000;
}

// Stat card — refined per the redesign. `tint` drives colour family, `alert` adds pulsing dot.
function StatCard({ label, value, sub, tint = 'brand', alert, active, onClick }) {
  const tints = {
    brand: { val: 'text-brand-700', ring: 'ring-gray-200',  dot: 'bg-brand-500',  activeRing: 'ring-brand-400' },
    amber: { val: 'text-amber-600', ring: 'ring-gray-200',  dot: 'bg-amber-500',  activeRing: 'ring-amber-400' },
    rose:  { val: 'text-rose-600',  ring: 'ring-rose-200',  dot: 'bg-rose-500',   activeRing: 'ring-rose-500'  },
  };
  const t = tints[tint] || tints.brand;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left bg-white rounded-xl ring-1 px-5 py-4 relative overflow-hidden transition-all ${active ? `ring-2 ${t.activeRing}` : t.ring} ${onClick ? 'hover:ring-brand-300 cursor-pointer' : ''}`}
    >
      {alert && <span className="absolute top-3 right-3 w-2 h-2 rounded-full bg-rose-500 animate-pulse" aria-hidden="true" />}
      <p className="text-[15px] 2xl:text-[16px] uppercase tracking-wider text-gray-500 font-semibold">{label}</p>
      <p className={`text-[44px] 2xl:text-[52px] font-bold tabular leading-none mt-2 ${t.val}`}>{value ?? 0}</p>
      {sub && (
        <p className="text-[14.5px] text-gray-500 mt-1.5 flex items-center gap-1.5">
          <span className={`w-1.5 h-1.5 rounded-full ${t.dot}`} aria-hidden="true" />
          {sub}
        </p>
      )}
    </button>
  );
}

// Submission row — bento-style grid: company/changes | submitted | progress | status | action.
// Count of a client's open payroll notes, shown next to their name.
function NotesBadge({ count }) {
  if (!count) return null;
  return (
    <span className="px-1.5 py-0.5 bg-amber-100 text-amber-800 text-[12px] font-semibold rounded" title="Open payroll notes">
      {count} note{count === 1 ? '' : 's'}
    </span>
  );
}

function SubmissionRow({ submission, viewMonth, viewYear, openNotes = 0 }) {
  const data = typeof submission.changes_data === 'string'
    ? JSON.parse(submission.changes_data)
    : submission.changes_data || {};
  const changes = data.changes || [];
  const commissions = (data.commissions || []).filter(c => c.commission > 0);
  const status = submission.processed ? 'processed' : (submission.submission_status || 'pending');
  const isNew = !submission.processed && isNewSubmission(submission.submitted_at);

  const checklist = submission.processing_checklist || {};
  const runsheetEntries = Object.keys(checklist).filter(k => k.startsWith('runsheet_'));
  const doneCount = runsheetEntries.filter(k => checklist[k]).length;
  const totalCount = runsheetEntries.length;
  const showProgress = !submission.processed && totalCount > 0;

  const payDate = viewMonth && viewYear ? getPayDayDate(submission.pay_day_type, submission.pay_day_specific_date, viewMonth, viewYear, submission.pay_day_adjust) : null;
  const daysUntilPay = payDate ? Math.ceil((payDate - new Date()) / (1000 * 60 * 60 * 24)) : null;

  return (
    <Link
      to={`/admin/process/${submission.id}`}
      className="block bg-white ring-1 ring-gray-200 hover:ring-brand-300 hover:shadow-sm transition-all rounded-xl px-5 py-3.5"
    >
      <div className="grid items-center gap-4" style={{ gridTemplateColumns: 'minmax(0,1fr) 220px 160px 100px 130px' }}>
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-[17px] 2xl:text-[18px] font-semibold text-ink truncate">{submission.company_name}</span>
            {isNew && <span className="px-1.5 py-0.5 bg-brand-600 text-white text-[12px] font-bold rounded uppercase">New</span>}
            <NotesBadge count={openNotes} />
          </div>
          <p className="text-[15px] text-gray-500 truncate">
            {getChangeSummary(changes)}{commissions.length > 0 && `, ${commissions.length} commission(s)`}
          </p>
        </div>
        <div className="text-[14.5px] text-gray-500">
          <p className="tabular">{new Date(submission.submitted_at).toLocaleString('en-GB')}</p>
          {submission.tax_year && (
            <p className="text-brand-700 font-medium">Tax {submission.tax_year}/{submission.tax_year + 1} M{submission.tax_month}</p>
          )}
        </div>
        <div className="min-w-0">
          {showProgress ? (
            <>
              <p className="text-[13px] uppercase tracking-wider text-gray-500 font-semibold">Entered</p>
              <div className="flex items-center gap-2 mt-1">
                <div className="flex-1 min-w-0"><ProgressBar done={doneCount} total={totalCount} height={5} /></div>
                <span className="text-[14px] tabular text-gray-700 font-semibold whitespace-nowrap">{doneCount}/{totalCount}</span>
              </div>
            </>
          ) : payDate ? (
            <>
              <p className="text-[13px] uppercase tracking-wider text-gray-500 font-semibold">Pay date</p>
              <p className="text-[14.5px] text-ink font-medium mt-0.5">
                {formatPayDayDate(payDate)}
                {!submission.processed && daysUntilPay !== null && daysUntilPay >= 0 && (
                  <span className="text-gray-500 font-normal"> · {daysUntilPay === 0 ? 'today' : `${daysUntilPay}d`}</span>
                )}
              </p>
            </>
          ) : null}
        </div>
        <StatusPill status={status} />
        <span className={`px-4 py-2 rounded-md text-[15px] font-semibold transition-colors text-center ${
          submission.processed
            ? 'bg-brand-50 text-brand-700 hover:bg-brand-100 ring-1 ring-brand-200'
            : 'bg-brand-600 text-white hover:bg-brand-700'
        }`}>
          {submission.processed ? 'View' : 'Process →'}
        </span>
      </div>
    </Link>
  );
}

function GroupHeader({ title, count, tint = 'brand' }) {
  const tintBg = { brand: 'bg-brand-500', amber: 'bg-amber-500', rose: 'bg-rose-500', emerald: 'bg-emerald-500' }[tint] || 'bg-brand-500';
  return (
    <div className="flex items-center gap-3 mb-3 mt-9 first:mt-0">
      <span className={`w-1 h-5 ${tintBg} rounded-full`} aria-hidden="true" />
      <h3 className="text-[15px] font-bold tracking-[0.12em] uppercase text-ink">{title}</h3>
      <span className="text-gray-400 text-[15px] tabular">({count})</span>
    </div>
  );
}

// Group submissions by pay date and order overdue/this-week/upcoming groups first.
function groupByPayDate(submissions, viewMonth, viewYear) {
  const now = new Date();
  const groups = new Map();
  for (const sub of submissions) {
    const payDate = getPayDayDate(sub.pay_day_type, sub.pay_day_specific_date, viewMonth, viewYear, sub.pay_day_adjust);
    const key = payDate ? payDate.toISOString().split('T')[0] : 'unknown';
    if (!groups.has(key)) groups.set(key, { date: payDate, rows: [] });
    groups.get(key).rows.push(sub);
  }
  const arr = Array.from(groups.entries()).map(([key, g]) => {
    const daysUntil = g.date ? Math.ceil((g.date - now) / (1000 * 60 * 60 * 24)) : 999;
    let title, tint;
    if (key === 'unknown') {
      title = 'No pay date set';
      tint = 'amber';
    } else if (daysUntil < 0) {
      title = `Already paid · ${formatPayDayDate(g.date)}`;
      tint = 'rose';
    } else if (daysUntil <= 5) {
      title = `Due in next 5 working days · ${formatPayDayDate(g.date)}`;
      tint = 'amber';
    } else {
      title = `Due ${formatPayDayDate(g.date)}`;
      tint = 'brand';
    }
    return { key, title, tint, rows: g.rows, daysUntil };
  });
  arr.sort((a, b) => a.daysUntil - b.daysUntil);
  return arr;
}

export default function AdminDashboard() {
  const { user, viewAsClient } = useAuth();
  const isAdmin = user?.role === 'admin';
  const now = new Date();
  const [stats, setStats] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [notSubmitted, setNotSubmitted] = useState([]);
  const [handledOutside, setHandledOutside] = useState([]);
  const [handledTarget, setHandledTarget] = useState(null);
  const [payrollNotes, setPayrollNotes] = useState([]);
  const [viewMonth, setViewMonth] = useState(now.getMonth() + 1);
  const [viewYear, setViewYear] = useState(now.getFullYear());
  const [filters, setFilters] = useState({ company_id: '', processed: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('submitted_at');
  const [sortDir, setSortDir] = useState('desc');
  const [activeTab, setActiveTab] = useState('submissions');
  const [loading, setLoading] = useState(true);
  const [sendingReminder, setSendingReminder] = useState({});
  const [chasingAll, setChasingAll] = useState(false);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [notSubmittedSort, setNotSubmittedSort] = useState('deadline_asc');
  const [notSubmittedSearch, setNotSubmittedSearch] = useState('');
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, totalCount: 0 });
  const [recentDocs, setRecentDocs] = useState([]);
  const [pendingPensionChanges, setPendingPensionChanges] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [toast, setToast] = useState({ message: '', type: 'error' });
  const [confirmAction, setConfirmAction] = useState(null);

  const goBack = () => {
    if (viewMonth === 1) { setViewMonth(12); setViewYear(viewYear - 1); }
    else { setViewMonth(viewMonth - 1); }
  };
  const goForward = () => {
    if (viewMonth === 12) { setViewMonth(1); setViewYear(viewYear + 1); }
    else { setViewMonth(viewMonth + 1); }
  };

  const loadPayrollNotes = useCallback(() => (
    api.get('/admin/payroll-notes', { params: { month: viewMonth, year: viewYear } })
      .then(r => setPayrollNotes(r.data))
      .catch(() => setPayrollNotes([]))
  ), [viewMonth, viewYear]);

  useEffect(() => { loadPayrollNotes(); }, [loadPayrollNotes]);

  const openNotesByCompany = payrollNotes.reduce((acc, n) => {
    if (!n.done) acc[n.company_id] = (acc[n.company_id] || 0) + 1;
    return acc;
  }, {});
  const openNotesCount = payrollNotes.filter(n => !n.done).length;

  const handleAddPayrollNote = async ({ company_id, note }) => {
    await api.post('/admin/payroll-notes', { company_id, note, month: viewMonth, year: viewYear });
    await loadPayrollNotes();
  };

  const handleTogglePayrollNote = async (n) => {
    try {
      await api.patch(`/admin/payroll-notes/${n.id}`, { done: !n.done });
      await loadPayrollNotes();
    } catch (err) {
      setToast({ message: 'Could not update note: ' + (err.response?.data?.error || err.message), type: 'error' });
    }
  };

  const handleDeletePayrollNote = (n) => {
    setConfirmAction({
      title: 'Delete note',
      message: `Delete this note for ${n.company_name}?\n\n"${n.note}"`,
      confirmText: 'Delete',
      confirmStyle: 'danger',
      onConfirm: async () => {
        setConfirmAction(null);
        try {
          await api.delete(`/admin/payroll-notes/${n.id}`);
          await loadPayrollNotes();
        } catch (err) {
          setToast({ message: 'Could not delete note: ' + (err.response?.data?.error || err.message), type: 'error' });
        }
      },
    });
  };

  const loadData = useCallback(async () => {
    try {
      setLoadError('');
      const [statsRes, subsRes, compRes, notSubRes] = await Promise.all([
        api.get('/admin/stats', { params: { month: viewMonth, year: viewYear } }),
        api.get('/admin/submissions', { params: { ...filters, month: viewMonth, year: viewYear } }),
        api.get('/admin/companies'),
        api.get('/admin/not-submitted', { params: { month: viewMonth, year: viewYear } }),
      ]);
      setStats(statsRes.data);
      if (subsRes.data.submissions) {
        setSubmissions(subsRes.data.submissions);
        setPagination(subsRes.data.pagination);
      } else {
        setSubmissions(subsRes.data);
      }
      setCompanies(compRes.data);
      setNotSubmitted(notSubRes.data);
      setSelectedCompanies([]);
      api.get('/documents/admin/recent').then(r => setRecentDocs(r.data)).catch(() => {});
      api.get('/admin/handled-outside-portal', { params: { month: viewMonth, year: viewYear } }).then(r => setHandledOutside(r.data)).catch(() => setHandledOutside([]));
      api.get('/admin/pending-pension-changes').then(r => setPendingPensionChanges(r.data)).catch(() => {});
    } catch (err) {
      console.error('Failed to load admin data:', err);
      setLoadError('Failed to load admin data. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, [filters, viewMonth, viewYear]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSendReminder = async (companyId) => {
    setSendingReminder(prev => ({ ...prev, [companyId]: true }));
    try {
      const res = await api.post(`/admin/send-reminder/${companyId}`, { month: viewMonth, year: viewYear });
      setToast({ message: res.data.message, type: 'success' });
    } catch (err) {
      setToast({ message: 'Failed to send reminder: ' + (err.response?.data?.error || err.message), type: 'error' });
    } finally {
      setSendingReminder(prev => ({ ...prev, [companyId]: false }));
    }
  };

  const handleMarkHandled = async ({ reason, note }) => {
    const company = handledTarget;
    await api.post('/admin/handled-outside-portal', { company_id: company.id, month: viewMonth, year: viewYear, reason, note });
    setHandledTarget(null);
    setSelectedCompanies(prev => prev.filter(id => id !== company.id));
    setToast({ message: `${company.company_name} marked as handled outside the portal`, type: 'success' });
    loadData();
  };

  const handleUndoHandled = (row) => {
    setConfirmAction({
      title: 'Move back to Not Submitted',
      message: `Put ${row.company_name} back on the Not Submitted list for ${MONTH_NAMES[viewMonth - 1]} ${viewYear}? Automatic reminders for this month will resume.`,
      confirmText: 'Move back',
      confirmStyle: 'warning',
      onConfirm: async () => {
        setConfirmAction(null);
        try {
          await api.delete(`/admin/handled-outside-portal/${row.id}`);
          loadData();
        } catch (err) {
          setToast({ message: 'Undo failed: ' + (err.response?.data?.error || err.message), type: 'error' });
        }
      },
    });
  };

  const handleChaseAll = () => {
    setConfirmAction({
      title: 'Send reminders to every client',
      message: `Send a payroll reminder email to all ${notSubmitted.length} clients who haven't submitted for ${MONTH_NAMES[viewMonth - 1]} ${viewYear}?`,
      confirmText: `Send ${notSubmitted.length} reminders`,
      confirmStyle: 'primary',
      onConfirm: async () => {
        setConfirmAction(null);
        setChasingAll(true);
        try {
          const res = await api.post('/admin/submissions/chase-all', { month: viewMonth, year: viewYear });
          setToast({ message: `Sent ${res.data.sent} of ${res.data.total} reminder${res.data.total === 1 ? '' : 's'}`, type: 'success' });
        } catch (err) {
          setToast({ message: 'Chase failed: ' + (err.response?.data?.error || err.message), type: 'error' });
        } finally {
          setChasingAll(false);
        }
      },
    });
  };

  const handleSendSelectedReminders = () => {
    const selected = notSubmitted.filter(c => selectedCompanies.includes(c.id));
    setConfirmAction({
      title: 'Send Reminders',
      message: `Send a reminder to ${selected.length} selected client${selected.length === 1 ? '' : 's'}?`,
      confirmText: 'Send Reminders',
      confirmStyle: 'primary',
      onConfirm: async () => {
        setConfirmAction(null);
        for (const company of selected) { await handleSendReminder(company.id); }
        setSelectedCompanies([]);
      },
    });
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams({ ...filters, month: viewMonth, year: viewYear }).toString();
      const res = await api.get(`/admin/export/csv?${params}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `submissions-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setToast({ message: 'Failed to export: ' + (err.response?.data?.error || err.message), type: 'error' });
    }
  };

  if (loading) {
    return (
      <div className="bg-[#f6f3fb] dark:bg-gray-900 min-h-screen">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center text-gray-500">
          Loading admin dashboard...
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="bg-[#f6f3fb] dark:bg-gray-900 min-h-screen">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <p className="text-gray-700 font-medium">{loadError}</p>
          <button onClick={() => { setLoading(true); loadData(); }} className="mt-4 px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-md hover:bg-brand-700">
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Filter + sort the submissions list before grouping
  let filtered = submissions;
  if (searchTerm.trim()) {
    const term = searchTerm.toLowerCase();
    filtered = filtered.filter(s => s.company_name?.toLowerCase().includes(term));
  }
  filtered = [...filtered].sort((a, b) => {
    let cmp = 0;
    if (sortField === 'company_name') {
      cmp = (a.company_name || '').localeCompare(b.company_name || '');
    } else {
      cmp = new Date(a.submitted_at) - new Date(b.submitted_at);
    }
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const grouped = groupByPayDate(filtered, viewMonth, viewYear);

  return (
    <div className="bg-[#f6f3fb] min-h-screen">
      <div className="max-w-[1600px] 2xl:max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: '', type: 'error' })} />

      {/* Header row */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-[32px] 2xl:text-[38px] text-ink leading-none">Admin Dashboard</h1>
        <div className="flex items-center gap-1 bg-white ring-1 ring-gray-200 rounded-lg px-1.5 py-1">
          <button onClick={goBack} aria-label="Previous month" className="w-7 h-7 grid place-items-center rounded-md hover:bg-gray-100 text-gray-600">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <span className="px-3 text-[17px] font-semibold text-ink min-w-[140px] text-center">{MONTH_NAMES[viewMonth - 1]} {viewYear}</span>
          <button onClick={goForward} aria-label="Next month" className="w-7 h-7 grid place-items-center rounded-md hover:bg-gray-100 text-gray-600">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleExport} className="px-3 py-2 rounded-md bg-white ring-1 ring-gray-200 text-[15.5px] font-medium text-gray-700 hover:bg-gray-50">
            Export CSV
          </button>
        </div>
      </div>

      {/* Stats row */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard
            label="Total Clients"
            value={stats.total_companies}
            sub="this period"
            tint="brand"
          />
          <StatCard
            label="Submissions"
            value={stats.total_submissions}
            sub="this month"
            tint="brand"
            active={activeTab === 'submissions' && filters.processed === ''}
            onClick={() => { setFilters({ company_id: '', processed: '' }); setActiveTab('submissions'); }}
          />
          <StatCard
            label="Unprocessed"
            value={stats.unprocessed_submissions}
            sub={stats.unprocessed_submissions > 0 ? 'awaiting payroll' : 'great, all clear'}
            tint="amber"
            active={activeTab === 'submissions' && filters.processed === 'false'}
            onClick={() => { setFilters(prev => ({ ...prev, processed: 'false', page: 1 })); setActiveTab('submissions'); }}
          />
          <StatCard
            label="Not Submitted"
            value={notSubmitted.length}
            sub={notSubmitted.length > 0 ? `${notSubmitted.filter(c => {
              const deadline = getSubmissionDeadline(c.pay_day_type, c.pay_day_specific_date, viewMonth, viewYear, c.deadline_days_before, c.pay_day_adjust);
              return deadline && deadline < new Date();
            }).length} overdue` : 'all in'}
            tint="rose"
            alert={notSubmitted.length > 0}
            active={activeTab === 'not-submitted'}
            onClick={() => (notSubmitted.length > 0 || handledOutside.length > 0) && setActiveTab('not-submitted')}
          />
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center border-b border-gray-200 mb-4">
        {[
          { key: 'submissions', label: 'Submissions', count: submissions.length, badge: null },
          { key: 'not-submitted', label: 'Not Submitted', count: notSubmitted.length, badge: notSubmitted.length > 0 ? 'rose' : null },
          { key: 'pension-changes', label: 'Pension Changes', count: pendingPensionChanges.length, badge: pendingPensionChanges.length > 0 ? 'amber' : null },
          { key: 'documents', label: 'Documents', count: recentDocs.length, badge: recentDocs.length > 0 ? 'sky' : null },
          { key: 'notes', label: 'Notes', count: openNotesCount, badge: openNotesCount > 0 ? 'amber' : null },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`px-4 py-3 text-[16px] font-medium border-b-2 transition-colors -mb-px inline-flex items-center gap-2 ${
              activeTab === t.key ? 'border-brand-600 text-brand-700' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
            {t.count > 0 && (
              <span className={`px-1.5 py-0.5 rounded-full text-[12.5px] font-bold ${
                t.badge === 'rose' ? 'bg-rose-500 text-white'
                : t.badge === 'amber' ? 'bg-amber-500 text-white'
                : t.badge === 'sky'  ? 'bg-sky-500 text-white'
                : activeTab === t.key ? 'bg-brand-100 text-brand-700' : 'bg-gray-200 text-gray-700'
              }`}>{t.count}</span>
            )}
          </button>
        ))}
      </div>

      {activeTab === 'submissions' && (
        <div>
          {/* Slim filter strip */}
          <div className="bg-white ring-1 ring-gray-200 rounded-xl px-4 py-3 mb-5 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 flex-1 min-w-[200px]">
              <svg className="text-gray-400 shrink-0" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search company name…"
                className="flex-1 border-0 p-0 text-[16px] focus:ring-0 placeholder:text-gray-400 bg-transparent"
              />
            </div>
            <span className="w-px h-5 bg-gray-200" aria-hidden="true" />
            <div className="min-w-[200px]">
              <SearchableSelect
                value={filters.company_id}
                onChange={(val) => setFilters(prev => ({ ...prev, company_id: val, page: 1 }))}
                placeholder="All Companies"
                options={companies.map(c => ({ value: c.id, label: c.company_name }))}
              />
            </div>
            <select
              value={filters.processed}
              onChange={(e) => setFilters(prev => ({ ...prev, processed: e.target.value, page: 1 }))}
              className="rounded-md ring-1 ring-gray-200 border-0 text-[15px] py-1.5 pl-3 pr-8 focus:ring-brand-500"
            >
              <option value="">All statuses</option>
              <option value="false">Unprocessed</option>
              <option value="true">Processed</option>
            </select>
            <button
              onClick={() => {
                if (sortField === 'submitted_at') setSortDir(d => d === 'asc' ? 'desc' : 'asc');
                else { setSortField('submitted_at'); setSortDir('desc'); }
              }}
              className={`px-3 py-1.5 rounded-md ring-1 text-[14.5px] font-medium ${sortField === 'submitted_at' ? 'ring-brand-200 bg-brand-50 text-brand-700' : 'ring-gray-200 bg-white text-gray-700 hover:bg-gray-50'}`}
            >
              Date {sortField === 'submitted_at' && (sortDir === 'asc' ? '↑' : '↓')}
            </button>
            <button
              onClick={() => {
                if (sortField === 'company_name') setSortDir(d => d === 'asc' ? 'desc' : 'asc');
                else { setSortField('company_name'); setSortDir('asc'); }
              }}
              className={`px-3 py-1.5 rounded-md ring-1 text-[14.5px] font-medium ${sortField === 'company_name' ? 'ring-brand-200 bg-brand-50 text-brand-700' : 'ring-gray-200 bg-white text-gray-700 hover:bg-gray-50'}`}
            >
              Name {sortField === 'company_name' && (sortDir === 'asc' ? '↑' : '↓')}
            </button>
          </div>

          {/* Grouped submission list */}
          {filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-500 bg-white ring-1 ring-gray-200 rounded-xl">
              <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="font-medium">No submissions found</p>
              <p className="text-sm mt-1">
                {searchTerm.trim() ? 'No matching submissions.' : `No payroll changes have been submitted for ${MONTH_NAMES[viewMonth - 1]} ${viewYear}.`}
              </p>
            </div>
          ) : (
            <>
              {grouped.map(group => (
                <div key={group.key}>
                  <GroupHeader title={group.title} count={group.rows.length} tint={group.tint} />
                  <div className="space-y-2.5">
                    {group.rows.map(sub => (
                      <SubmissionRow key={sub.id} submission={sub} viewMonth={viewMonth} viewYear={viewYear} openNotes={openNotesByCompany[sub.company_id] || 0} />
                    ))}
                  </div>
                </div>
              ))}
              {pagination.totalPages > 1 && (
                <div className="flex items-center justify-between pt-4 mt-4 border-t border-gray-200">
                  <span className="text-sm text-gray-600">
                    Page {pagination.page} of {pagination.totalPages} ({pagination.totalCount} total)
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setFilters(f => ({ ...f, page: pagination.page - 1 }))}
                      disabled={pagination.page <= 1}
                      className="px-3 py-1 text-sm rounded-md ring-1 ring-gray-200 disabled:opacity-50 hover:bg-gray-50"
                    >Previous</button>
                    <button
                      onClick={() => setFilters(f => ({ ...f, page: pagination.page + 1 }))}
                      disabled={pagination.page >= pagination.totalPages}
                      className="px-3 py-1 text-sm rounded-md ring-1 ring-gray-200 disabled:opacity-50 hover:bg-gray-50"
                    >Next</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {activeTab === 'not-submitted' && (() => {
        const filteredNs = notSubmittedSearch
          ? notSubmitted.filter(c => c.company_name.toLowerCase().includes(notSubmittedSearch.toLowerCase()))
          : notSubmitted;
        const enriched = filteredNs.map(c => {
          const deadlineDate = getSubmissionDeadline(c.pay_day_type, c.pay_day_specific_date, viewMonth, viewYear, c.deadline_days_before, c.pay_day_adjust);
          const payDate = getPayDayDate(c.pay_day_type, c.pay_day_specific_date, viewMonth, viewYear, c.pay_day_adjust);
          const daysUntilDeadline = deadlineDate ? Math.ceil((deadlineDate - new Date()) / (1000 * 60 * 60 * 24)) : 999;
          return { ...c, deadlineDate, payDate, daysUntilDeadline };
        });
        const sorted = [...enriched].sort((a, b) => {
          if (notSubmittedSort === 'company') return (a.company_name || '').localeCompare(b.company_name || '');
          const cmp = (a.daysUntilDeadline) - (b.daysUntilDeadline);
          return notSubmittedSort === 'deadline_desc' ? -cmp : cmp;
        });
        const overdue = sorted.filter(c => c.daysUntilDeadline < 0);
        const urgent = sorted.filter(c => c.daysUntilDeadline >= 0 && c.daysUntilDeadline <= 5);

        return (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
          {/* Main not-submitted list */}
          <div className="bg-white ring-1 ring-gray-200 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between gap-3 flex-wrap">
              <div>
                <h3 className="text-[16px] font-semibold text-ink">Clients not yet submitted for {MONTH_NAMES[viewMonth - 1]} {viewYear}</h3>
                <p className="text-[14px] text-gray-500 mt-0.5">
                  {[overdue.length > 0 && `${overdue.length} overdue`, urgent.length > 0 && `${urgent.length} due this week`].filter(Boolean).join(' · ') || 'All clients have submitted'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={notSubmittedSearch}
                  onChange={(e) => setNotSubmittedSearch(e.target.value)}
                  placeholder="Search…"
                  className="rounded-md ring-1 ring-gray-200 border-0 text-[14.5px] py-1.5 px-2.5 w-40 focus:ring-brand-500"
                />
                {selectedCompanies.length > 0 && isAdmin && (
                  <button
                    onClick={handleSendSelectedReminders}
                    className="px-3 py-1.5 text-[14.5px] font-medium text-white bg-amber-500 rounded-md hover:bg-amber-600"
                  >
                    Remind {selectedCompanies.length}
                  </button>
                )}
              </div>
            </div>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {isAdmin && (
                    <th className="px-4 py-3 text-center w-10">
                      <input
                        type="checkbox"
                        checked={selectedCompanies.length > 0 && selectedCompanies.length === sorted.length}
                        onChange={(e) => setSelectedCompanies(e.target.checked ? sorted.map(c => c.id) : [])}
                        className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                      />
                    </th>
                  )}
                  <th className="px-4 py-3 text-left text-[13.5px] font-bold uppercase tracking-wider text-gray-600 cursor-pointer select-none hover:text-brand-600"
                    onClick={() => setNotSubmittedSort(prev => prev === 'company' ? 'deadline_asc' : 'company')}>
                    Company {notSubmittedSort === 'company' && '▲'}
                  </th>
                  <th className="px-4 py-3 text-left text-[13.5px] font-bold uppercase tracking-wider text-gray-600">Contact</th>
                  <th className="px-4 py-3 text-left text-[13.5px] font-bold uppercase tracking-wider text-gray-600">Pay Day</th>
                  <th className="px-4 py-3 text-left text-[13.5px] font-bold uppercase tracking-wider text-gray-600 cursor-pointer select-none hover:text-brand-600"
                    onClick={() => setNotSubmittedSort(prev => prev === 'deadline_asc' ? 'deadline_desc' : 'deadline_asc')}>
                    Deadline {notSubmittedSort === 'deadline_asc' ? '▲' : notSubmittedSort === 'deadline_desc' ? '▼' : ''}
                  </th>
                  <th className="px-4 py-3 text-center text-[13.5px] font-bold uppercase tracking-wider text-gray-600">Days</th>
                  <th className="px-4 py-3 text-center text-[13.5px] font-bold uppercase tracking-wider text-gray-600">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sorted.map(company => {
                  const deadlinePassed = company.daysUntilDeadline < 0;
                  const isSelected = selectedCompanies.includes(company.id);
                  const rowBg = deadlinePassed ? 'bg-rose-50/40' : company.daysUntilDeadline <= 5 ? 'bg-amber-50/40' : '';
                  const daysLabel = deadlinePassed
                    ? `${Math.abs(company.daysUntilDeadline)}d overdue`
                    : company.daysUntilDeadline === 0 ? 'Today' : `${company.daysUntilDeadline}d`;
                  const daysColor = deadlinePassed ? 'text-rose-700 font-bold'
                    : company.daysUntilDeadline <= 2 ? 'text-rose-600 font-semibold'
                    : company.daysUntilDeadline <= 5 ? 'text-amber-600 font-medium'
                    : 'text-emerald-600';
                  return (
                    <tr key={company.id} className={`hover:bg-gray-50 ${rowBg}`}>
                      {isAdmin && (
                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => setSelectedCompanies(prev => e.target.checked ? [...prev, company.id] : prev.filter(id => id !== company.id))}
                            className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                          />
                        </td>
                      )}
                      <td className="px-4 py-3 text-[16px] font-medium text-ink">
                        <span className="inline-flex items-center gap-2">{company.company_name} <NotesBadge count={openNotesByCompany[company.id] || 0} /></span>
                      </td>
                      <td className="px-4 py-3 text-[15.5px] text-gray-600">
                        {company.contact_first_name ? `${company.contact_first_name} ${company.contact_last_name || ''}`.trim() : company.contact_name}
                      </td>
                      <td className="px-4 py-3 text-[15.5px] text-gray-600">
                        <span className="px-2 py-1 bg-brand-50 text-brand-700 text-[14px] rounded font-medium">{formatPayDayDate(company.payDate)}</span>
                      </td>
                      <td className="px-4 py-3 text-[15.5px]">
                        {company.deadlineDate ? (
                          <span className={`px-2 py-1 text-[14px] rounded font-medium ${deadlinePassed ? 'bg-rose-100 text-rose-700' : 'bg-sky-50 text-sky-700'}`}>
                            {formatPayDayDate(company.deadlineDate)}
                          </span>
                        ) : <span className="text-[14px] text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`text-[14px] ${daysColor}`}>{daysLabel}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          {isAdmin && (
                            <button
                              onClick={() => handleSendReminder(company.id)}
                              disabled={sendingReminder[company.id]}
                              className="px-3 py-1.5 text-[14px] font-medium text-white bg-amber-500 rounded hover:bg-amber-600 disabled:opacity-50"
                            >
                              {sendingReminder[company.id] ? 'Sending…' : 'Remind'}
                            </button>
                          )}
                          <button
                            onClick={() => setHandledTarget(company)}
                            title="Payroll run without a portal submission (changes by email, no changes)"
                            className="px-3 py-1.5 text-[14px] font-medium text-brand-700 ring-1 ring-brand-200 rounded hover:bg-brand-50 whitespace-nowrap"
                          >
                            Handled
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {sorted.length === 0 && (
                  <tr>
                    <td colSpan={isAdmin ? 7 : 6} className="px-4 py-10 text-center text-gray-500">
                      All clients have submitted for {MONTH_NAMES[viewMonth - 1]} {viewYear}.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {handledOutside.length > 0 && (
              <div className="border-t border-gray-200">
                <div className="px-4 py-3 bg-gray-50">
                  <h4 className="text-[14.5px] font-semibold text-ink">Handled outside the portal ({handledOutside.length})</h4>
                  <p className="text-[13.5px] text-gray-500">Run for {MONTH_NAMES[viewMonth - 1]} without a portal submission. No reminders are sent for these.</p>
                </div>
                <ul className="divide-y divide-gray-100">
                  {handledOutside.map(h => (
                    <li key={h.id} className="px-4 py-3 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-[15.5px] font-medium text-ink">{h.company_name}</p>
                        <p className="text-[13.5px] text-gray-500">
                          {handledReasonLabel(h.reason)}
                          {h.note ? ` — ${h.note}` : ''}
                          {' · '}
                          {h.marked_by_name ? `${h.marked_by_name}, ` : ''}
                          {new Date(h.marked_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                        </p>
                      </div>
                      <button
                        onClick={() => handleUndoHandled(h)}
                        className="text-[14px] text-gray-500 hover:text-gray-800 hover:underline shrink-0"
                      >
                        Undo
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Chase sidebar — gradient CTA tile */}
          {notSubmitted.length > 0 && isAdmin && (
            <div className="bg-gradient-to-br from-rose-500 to-rose-700 text-white rounded-xl p-5 shadow-lg h-fit sticky top-4">
              <p className="text-[13.5px] uppercase tracking-wider opacity-80 font-semibold">Chase these clients</p>
              <p className="text-[40px] font-bold tabular leading-none mt-1.5">{notSubmitted.length}</p>
              <p className="text-[14.5px] opacity-80 mt-1">
                {overdue.length > 0 ? `${overdue.length} overdue · ` : ''}
                {urgent.length > 0 ? `${urgent.length} due in next 5 days` : 'none urgent yet'}
              </p>
              <button
                onClick={handleChaseAll}
                disabled={chasingAll}
                className="mt-4 w-full px-4 py-2.5 rounded-md bg-white text-rose-700 text-[16px] font-semibold hover:bg-rose-50 disabled:opacity-60"
              >
                {chasingAll ? 'Sending…' : `Send all ${notSubmitted.length} reminders`}
              </button>
              <p className="text-[13.5px] opacity-70 mt-2 leading-snug">
                Reuses the standard reminder template. Skips clients with no email on file.
              </p>
            </div>
          )}
        </div>
        );
      })()}

      {activeTab === 'pension-changes' && (
        <div className="bg-white ring-1 ring-gray-200 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <h3 className="text-[16px] font-semibold text-ink">Unsubmitted pension changes</h3>
            <p className="text-[14px] text-gray-500 mt-0.5">
              Pension edits clients have made that haven't been submitted for a payroll run yet. These already
              apply to the live pension record but don't trigger a notification — review them so nothing is missed.
            </p>
          </div>
          {pendingPensionChanges.length === 0 ? (
            <div className="p-8 text-center text-[14.5px] text-gray-500">No unsubmitted pension changes.</div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-[13.5px] font-bold uppercase tracking-wider text-gray-600">Client</th>
                  <th className="px-4 py-3 text-left text-[13.5px] font-bold uppercase tracking-wider text-gray-600">Employee</th>
                  <th className="px-4 py-3 text-left text-[13.5px] font-bold uppercase tracking-wider text-gray-600">Status Change</th>
                  <th className="px-4 py-3 text-left text-[13.5px] font-bold uppercase tracking-wider text-gray-600">Changed</th>
                  <th className="px-4 py-3 text-center text-[13.5px] font-bold uppercase tracking-wider text-gray-600">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pendingPensionChanges.map(pc => {
                  const changed = pc.previous_status && pc.previous_status !== pc.new_status;
                  return (
                    <tr key={pc.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-[14.5px] font-medium text-ink">{pc.company_name}</td>
                      <td className="px-4 py-3 text-[14.5px] text-gray-700">{pc.employee_name}</td>
                      <td className="px-4 py-3 text-[14.5px]">
                        {changed ? (
                          <span className="text-ink">
                            <span className="text-gray-500">{pc.previous_status}</span>
                            <span className="mx-1.5 text-amber-600">→</span>
                            <span className="font-semibold">{pc.new_status}</span>
                          </span>
                        ) : (
                          <span className="text-gray-700">{pc.new_status || '—'}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-[14px] text-gray-500 tabular">
                        {pc.created_at ? new Date(pc.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => viewAsClient(pc.company_id)}
                          className="px-3 py-1.5 text-[14px] font-medium text-brand-700 bg-brand-50 rounded-md hover:bg-brand-100"
                        >
                          View client
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === 'notes' && (
        <div className="bg-white ring-1 ring-gray-200 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <h3 className="text-[16px] font-semibold text-ink">Payroll notes — {MONTH_NAMES[viewMonth - 1]} {viewYear}</h3>
            <p className="text-[14px] text-gray-500 mt-0.5">
              The team's own notes on each client's payroll — what you're waiting on, what needs doing. Clients never see these.
              Open notes from earlier months stay here until they're ticked off.
            </p>
          </div>
          <div className="p-4 max-w-3xl">
            <PayrollNotes
              notes={payrollNotes}
              month={viewMonth}
              year={viewYear}
              companies={companies}
              canDelete={(n) => isAdmin || n.created_by === user?.id}
              onAdd={handleAddPayrollNote}
              onToggle={handleTogglePayrollNote}
              onDelete={handleDeletePayrollNote}
            />
          </div>
        </div>
      )}

      {activeTab === 'documents' && (
        <div className="bg-white ring-1 ring-gray-200 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <h3 className="text-[16px] font-semibold text-ink">Recent client document uploads</h3>
            <p className="text-[14px] text-gray-500 mt-0.5">Tax code notices, HMRC letters, and the P45s and starter checklists sent with new starters.</p>
          </div>
          {recentDocs.length === 0 ? (
            <p className="p-6 text-[15.5px] text-gray-500">No client documents uploaded yet.</p>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-[13.5px] font-bold uppercase tracking-wider text-gray-600">Client</th>
                  <th className="px-4 py-3 text-left text-[13.5px] font-bold uppercase tracking-wider text-gray-600">Employee</th>
                  <th className="px-4 py-3 text-left text-[13.5px] font-bold uppercase tracking-wider text-gray-600">Type</th>
                  <th className="px-4 py-3 text-left text-[13.5px] font-bold uppercase tracking-wider text-gray-600">File</th>
                  <th className="px-4 py-3 text-left text-[13.5px] font-bold uppercase tracking-wider text-gray-600">Uploaded</th>
                  <th className="px-4 py-3 text-left text-[13.5px] font-bold uppercase tracking-wider text-gray-600">Link</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {recentDocs.map(doc => (
                  <tr key={doc.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-[15.5px] font-medium text-ink">{doc.company_name}</td>
                    <td className="px-4 py-3 text-[15.5px] text-gray-600">
                      {doc.employee_id ? `${doc.first_name || ''} ${doc.last_name || ''}`.trim() : <span className="text-gray-400">Company</span>}
                    </td>
                    <td className="px-4 py-3 text-[15.5px] text-gray-600">
                      {({ tax_code_notice: 'Tax Code Notice', hmrc_letter: 'HMRC Letter', p45: 'P45', starter_checklist: 'Starter Checklist', contract: 'Contract', other: 'Other' })[doc.document_type] || doc.document_type}
                    </td>
                    <td className="px-4 py-3 text-[15.5px] text-gray-600">{doc.file_name}</td>
                    <td className="px-4 py-3 text-[15px] text-gray-500">
                      {new Date(doc.uploaded_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </td>
                    <td className="px-4 py-3 text-[15.5px]">
                      {doc.google_drive_url ? (
                        <a href={doc.google_drive_url} target="_blank" rel="noopener noreferrer" className="text-brand-700 hover:text-brand-800 font-medium">View</a>
                      ) : <span className="text-gray-400">Local</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {handledTarget && (
        <HandledOutsideModal
          company={handledTarget}
          monthLabel={`${MONTH_NAMES[viewMonth - 1]} ${viewYear}`}
          onSave={handleMarkHandled}
          onClose={() => setHandledTarget(null)}
        />
      )}

      {confirmAction && (
        <ConfirmModal
          title={confirmAction.title}
          message={confirmAction.message}
          confirmText={confirmAction.confirmText}
          confirmStyle={confirmAction.confirmStyle}
          onConfirm={confirmAction.onConfirm}
          onCancel={() => setConfirmAction(null)}
        />
      )}
      </div>
    </div>
  );
}
